import { useAuthContext } from "@/context/Auth";
import { Flex, PinInput, Stack, Text, Alert, Transition, Box } from "@mantine/core";
import { User } from "firebase/auth";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import Button from "../Button";
import FAIcon from "../FAIcon";

type OTPformProps = {
  user: User | null;
  onClose?: (verified?: boolean) => void;
  trustDevice?: boolean;
};

const INITIAL_RESEND_TIME = 120;
const SUBSEQUENT_RESEND_TIME = 300;

const OTPform: React.FC<OTPformProps> = ({ user, onClose, trustDevice = false }) => {
  const router = useRouter();
  const [value, setValue] = useState("");
  const [timeLeft, setTimeLeft] = useState(INITIAL_RESEND_TIME);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [canResend, setCanResend] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [timeExpired, setTimeExpired] = useState(false);
  const [resendCount, setResendCount] = useState(0);
  const { dispatch } = useAuthContext();

  useEffect(() => {
    if (timeLeft <= 0) {
      setCanResend(true);
      setTimeExpired(true);
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const handleAuthSuccess = async () => {
    try {
      // First store the trust status and verification status
      if (user) {
        if (trustDevice) {
          localStorage.setItem(`trusted_device_${user.uid}`, 'true');
        }
        localStorage.setItem(`2fa_verified_${user.uid}`, 'true');
        localStorage.setItem(`first_login_complete_${user.uid}`, 'true');
        // Clear pending OTP status after successful verification
        localStorage.removeItem(`pending_otp_${user.uid}`);
      }

      // Then update auth context
      dispatch({ 
        type: "2FA_ACCEPT",
        payload: {
          isTrustedDevice: trustDevice,
          isVerified: true
        }
      });

      onClose?.(true);

      // Navigate to profile
      try {
        await router.push("/profile");
      } catch (error) {
        console.error('Navigation error:', error);
        window.location.href = "/profile";
      }
    } catch (error) {
      console.error('Auth success handling error:', error);
      window.location.href = "/profile";
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (value.length !== 6 || !user) return;

    try {
      setIsVerifying(true);
      setError(null);
      const token = await user.getIdToken();

      const response = await fetch("/api/auth/verify-otp", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          otp: value,
          trustDevice
        })
      });

      const data = await response.json();
      if (!response.ok) {
        const errorMessage = data.error || 'Invalid verification code';
        throw new Error(errorMessage);
      }

      setSuccess("Verification successful! Redirecting...");

      // Handle auth success which includes storing trust status
      setTimeout(handleAuthSuccess, 1500);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Invalid verification code';
      setError(errorMessage);
      setValue("");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (!user || !canResend) {
      setError(`Please wait ${formatTime(timeLeft)} before requesting a new code.`);
      return;
    }

    try {
      setIsResending(true);
      setError(null);
      const token = await user.getIdToken();

      // Mark that user has a pending OTP
      localStorage.setItem(`pending_otp_${user.uid}`, 'true');

      const response = await fetch("/api/auth/create-otp", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          trustDevice,
          resend: true
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        // If server returns a timeLeft, user must wait
        if (data.timeLeft > 0) {
          setTimeLeft(data.timeLeft);
          setCanResend(false);
          setTimeExpired(false);
          throw new Error(`Please wait ${formatTime(data.timeLeft)} before requesting a new code.`);
        }
        throw new Error(data.error || 'Failed to send new code');
      }

      // Update timer based on API response
      if (data.timeLeft > 0) {
        setTimeLeft(data.timeLeft);
        setCanResend(false);
        setTimeExpired(false);
      }
      
      setResendCount(prev => prev + 1);
      setSuccess("New verification code sent!");
      setValue(""); // Clear the input
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send new code';
      setError(errorMessage);
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsResending(false);
    }
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack
        gap="xl"
        align="center"
        bg="white"
        p="xl"
        style={{
          borderRadius: '16px',
          boxShadow: '0 4px 20px -8px rgba(0, 0, 0, 0.1)',
          maxWidth: '440px',
          width: '100%',
        }}
      >
        {/* Status Alerts */}
        <Box w="100%">
          <Transition
            mounted={!!error}
            transition="slide-down"
            duration={400}
          >
            {(styles) => (
              <Alert
                style={styles}
                variant="light"
                color="red"
                title="Error"
                icon={<FAIcon name="exclamation-circle" size={16} />}
                withCloseButton
                onClose={() => setError(null)}
              >
                {error}
              </Alert>
            )}
          </Transition>

          <Transition
            mounted={!!success}
            transition="slide-down"
            duration={400}
          >
            {(styles) => (
              <Alert
                style={styles}
                variant="light"
                color="green"
                title="Success"
                icon={<FAIcon name="check-circle" size={16} />}
              >
                {success}
              </Alert>
            )}
          </Transition>
        </Box>

        {/* Header Section */}
        <Alert
          variant="light"
          color="blue"
          title="Verification Required"
          icon={<FAIcon name="envelope" size={20} />}
          styles={{
            root: { width: '100%' },
            wrapper: { gap: '8px' },
            title: { fontSize: '16px', fontWeight: 600 },
          }}
        >
          We&apos;ve sent a verification code to your email address
        </Alert>

        {/* Email notification with animation */}
        <Flex
          gap="sm"
          align="center"
          p="md"
          bg="blue.0"
          style={{
            borderRadius: '12px',
            animation: 'pulse 2s ease-in-out infinite',
            width: '100%',
          }}
        >
          <FAIcon name="envelope" size={20} color="#228be6" />
          <Stack gap={0}>
            <Text size="sm" fw={500} c="blue.9">
              Check your email
            </Text>
            <Text size="xs" c="gray.6">
              {user?.email}
            </Text>
          </Stack>
        </Flex>

        {/* OTP Input Section */}
        <Stack gap="md" w="100%" align="center">
          <Text size="sm" fw={500} c="gray.7" ta="center">
            Enter the 6-digit verification code
          </Text>

          <PinInput
            length={6}
            value={value}
            onChange={(val) => {
              setValue(val);
              if (val.length === 1 && value.length === 0) {
                setError(null);
              }
              if (val.length === 6) {
                handleSubmit();
              }
            }}
            size="lg"
            type="number"
            placeholder=""
            oneTimeCode
            manageFocus
            error={!!error}
            styles={(theme) => ({
              root: {
                gap: theme.spacing.sm,
              },
              input: {
                width: "2.75rem",
                height: "3rem",
                fontSize: "1.25rem",
                transition: "all 0.2s ease",
                borderColor: error ? theme.colors.red[6] : theme.colors.gray[3],
                "&:not(:placeholder-shown)": {
                  borderColor: error ? theme.colors.red[6] : theme.colors.blue[6],
                  background: theme.colors.blue[0],
                },
                "&:focus": {
                  borderColor: error ? theme.colors.red[6] : theme.colors.blue[6],
                  transform: "scale(1.05)",
                },
              },
            })}
          />
        </Stack>

        {/* Timer and Actions Section */}
        <Stack gap="xs" w="100%" align="center">
          <Flex direction="row" gap="sm" w="100%" justify={'space-between'}>
            <Button
              variant="filled"
              fullwidth
              size="medium"
              loading={isVerifying ? true : undefined}
              disabled={value.length !== 6 || isVerifying}
              leftSection={!isVerifying && <FAIcon name="lock" size={16} />}
              transition={false}
              component="button"
              expandOnHover={false}
            >
              {isVerifying ? "Verifying..." : "Verify Code"}
            </Button>
            <Stack>
              <Button
                variant="subtle"
                onClick={handleResend}
                fullwidth
                size="medium"
                loading={isResending ? true : undefined}
                disabled={!canResend || isResending}
                leftSection={!isResending && <FAIcon name="rotate" size={16} />}
                style={{
                  opacity: canResend ? 1 : 0.7,
                }}
                transition={false}
                component="button"
              >
                {isResending ? "Sending..." : timeExpired ? "Resend Code" : "Request New Code"}
              </Button>
              {!timeExpired && (
                <Text size="sm" c="gray.6" ta={'center'}>
                  Request new code in{' '}
                  <Text
                    span
                    fw={600}
                    c={timeLeft <= 30 ? "red" : "blue"}
                    style={{ letterSpacing: '0.5px' }}
                  >
                    {formatTime(timeLeft)}
                  </Text>
                </Text>
              )}
            </Stack>
          </Flex>
        </Stack>
      </Stack>
    </form>
  );
};

export default OTPform;